import {toNativeCurrencyString} from '@ttx/core';
import {useRouter} from 'next/router';
import React, {ReactElement, JSXElementConstructor, useMemo} from 'react';
import {WalletNetwork} from '../../contexts/authentication-context/types';
import {NftToken} from '../../server/router/collection/types';
import {NFT} from '../../server/router/nft/types';
import {traxxStemzSlug, emilGSlug, joshSavageSlug} from '../../utils/consts';
import {useMarketplaceContract} from './use-marketplace-contract';
import {useMultiNftContract} from './use-multi-nft-contract';
import {useTraxxStemzContract} from './use-traxxstemz-contract';
import {useEmilGContract} from './use-emilg-contract';
import {studioProperties} from '../../utils/studio-util';
import {useJoshSavageContract} from './use-josh-savage-contract';

const MOCK_NFT = ['low-lives', 'justify-acoustic'];

export const BlockchainPropsWrapper: React.FC<{
  multiCopy: boolean;
  network: WalletNetwork;
  type?: number;
  nftToken: NftToken | NFT;
  children: ReactElement<any, string | JSXElementConstructor<any>>;
}> = ({multiCopy, network, type, nftToken, children}) => {
  const {isStudioPage = false, studioSlug = ''} = studioProperties();
  const {_id, tokenId, contractAddress, status, slug, isStudio} = nftToken;
  const router = useRouter();
  const totalSupply = useMultiNftContract({
    tokenId,
    multiCopy,
    network,
  });

  const {currencySymbol, price, isSold, quantity} = useMarketplaceContract({
    tokenId,
    network,
    multiCopy,
    contractAddress,
  });

  // Not mutually exclusive
  const isTraxxStemz = type === 5;
  const isEmilG = type === 7;
  const isDraft = status === 1;
  const isMembership = type === 2;
  const isJoshSavage = type === 8;

  const {tokenLimit, mintPrice} = useTraxxStemzContract({
    network,
    isTraxxStemz,
  });

  const {tokenLimit: joshTokenLimit, mintPrice: joshMintPrice} =
    useJoshSavageContract({
      network,
      isJoshSavage,
    });

  const {tokenLimit: emilGtokenLimit, mintPrice: emilGMintPrice} =
    useEmilGContract({
      network,
      isEmilG,
    });

  const blindMint = !isTraxxStemz && !isEmilG && !isJoshSavage;

  // TODO: refactor blockchain props wrapper for cards vs hero cards vs nft details panel
  const state = useMemo(() => {
    if (slug === 'life-without-water') return 'sold out';
    if (MOCK_NFT.includes(nftToken.slug)) return 'available';
    if (isMembership) return 'membership';
    if (isTraxxStemz) return 'traxxStemz';
    if (isJoshSavage) return 'joshSavage';
    if (isEmilG) return 'emilg';
    if (isDraft) return 'coming soon';
    if (price === null && blindMint) return undefined;
    if (!price && blindMint) return 'coming soon';
    if (isSold) return 'sold out';
    return 'available';
  }, [
    isSold,
    price,
    isDraft,
    isTraxxStemz,
    isJoshSavage,
    isEmilG,
    isMembership,
  ]);

  const onPress = () => {
    if (isTraxxStemz) return router.push(`/traxxstemz/${traxxStemzSlug}`);
    if (isJoshSavage) return router.push(`/josh-savage/${joshSavageSlug}`);
    if (isEmilG) return router.push(`/emilg/${emilGSlug}`);
    let url = `/tracks/${slug}`;
    if (isStudioPage) url = `/studio/nft/${studioSlug}/${slug}`;
    return router.push(url);
  };

  let totalSply = totalSupply;
  if (isDraft || isStudio) totalSply = nftToken.originalQuantity.toString();
  else if (isTraxxStemz) totalSply = tokenLimit;
  else if (isJoshSavage) totalSply = joshTokenLimit;
  else if (isEmilG) totalSply = emilGtokenLimit;
  else totalSply = totalSupply;

  let symbol = currencySymbol;
  if (isTraxxStemz) symbol = 'ETH';
  else if (isJoshSavage) symbol = 'MATIC';
  else if (isEmilG || isMembership) symbol = 'TRAXX';

  let mintedPrice = price;
  if (isTraxxStemz)
    mintedPrice = toNativeCurrencyString(mintPrice as string, 'ETH');
  else if (isJoshSavage)
    mintedPrice = toNativeCurrencyString(joshMintPrice as string, 'MATIC');
  else if (isEmilG)
    mintedPrice = toNativeCurrencyString(emilGMintPrice as string, 'TRAXX');

  return React.cloneElement(children, {
    onPress,
    totalSupply: totalSply,
    currencySymbol: symbol,
    price: mintedPrice,
    isSold,
    multiCopy,
    network,
    tokenId: tokenId?.toString(),
    state,
    contractAddress,
    availableSupply: quantity?.toString(),
  });
};
